/**
 * The full column pass, phase 3 (`ColumnBindPasses.full`): apply the cells
 * and referee the dark scene's per-cell directives around that apply (the
 * ledger cross-check and the sampled fingerprints). Moved verbatim out of
 * `fullPass` (r5 FS 1).
 */
import type { RenderSkip } from "@tento-lona/sheets/platform";
import type { Option } from "@tento-lona/sheets";
import { nonnull } from "@tento-core/assert";
import { Tracing } from "@tento-core/tracing";
import { FrameMarks } from "@tento-ui/frame-marks";
import type { CellDirective, CommitBundle } from "./commit-bundle.ts";
import { refereeCells } from "./commit-referee.ts";
import type { CellRefereeSample, CellRefereeVerdict, CursorEvidence } from "./commit-referee.ts";
import { decideSkip, SkipVerdict } from "./skip-eligibility.ts";
import { type PendingCellSample } from "./cell-identity.ts";
import type { ColumnBindPort, ColumnPassInput } from "./column-bind-port.ts";
import type { FullPassApply } from "./full-pass-apply.ts";

/**
 * Translate a raw LR6 decision into what the ledger can actually TESTIFY.
 * `wouldSkip === false` has three causes and only one is a divergence; the
 * other two are the ledger declining to answer (no record yet, or no data
 * address at all — placement-only rows: pending / template / system /
 * container). Keeping them apart is what lets "referee at zero" mean
 * something.
 */
function cursorEvidence(
  decision: Option<RenderSkip.Decision>,
): CursorEvidence {
  if (decision == null) return { kind: "no-opinion", why: "no-cursor" };
  if (!decision.boundBefore) return { kind: "no-opinion", why: "never-bound" };
  if (decision.current == null) {
    return { kind: "no-opinion", why: "unresolvable" };
  }
  return decision.wouldSkip ? { kind: "agrees" } : { kind: "would-repaint" };
}

/**
 * Phase 3 of the full pass: apply `bundle`'s cells through the host and
 * referee `directives` (when the scene classified any). Returns the cell
 * referee's verdict, `null` when nothing was refereed.
 */
export function applyAndRefereeCells(
  port: ColumnBindPort,
  input: ColumnPassInput,
  apply: FullPassApply,
  bundle: Option<CommitBundle>,
  directives: Option<readonly CellDirective[]>,
): Option<CellRefereeVerdict> {
  const { defaultColumn, $cells, rows } = input;
  // Apply all plans synchronously. Spreading expensive binds across frames is
  // handled centrally by the ScrollEngine drain budget (it re-enqueues the tail
  // and drops indices scrolled past), not per-cell here — which is why the old
  // `deferOffscreen` requestIdleCallback path was retired.
  const activeBundle = nonnull(bundle, "commit-bundle: bundle missing for cell apply");
  const activeHost = apply.host;
  const cellReflowHonorable = apply.cellReflowHonorable;
  let cellVerdict: Option<CellRefereeVerdict> = null;

  // The dark per-cell referee. When directives were classified this pass, the
  // cell-apply bundle carries them (so a live SDK 2.0 switch honors
  // them; with the flag false applyCells rebinds every cell, byte-identical).
  // Capture LAYER-1 (the ledger cross-check, always-on for skips) + LAYER-2
  // (fingerprints straddling the STILL-EXECUTED full rebind, sampled at
  // cellRefereeRate) around the applyCells call, then referee.
  const cellApply: CommitBundle = directives != null
    ? { structure: activeBundle.structure, cells: directives }
    : activeBundle;

  const pending: PendingCellSample[] = [];
  // A census of WHY the one decider refused each `skip` claim, by cause.
  // This retires `skipsDowngraded`: that counter existed because the
  // pre-pass re-implemented the three DOM axes as `honorableOnDom` and had
  // nowhere to say so, and it could only ever name ONE of the causes. A
  // `decideSkip` verdict is the same fact with a name, so all three
  // buckets are reported and they sum to the refusals.
  const rebindBecause = SkipVerdict.emptyCauseCounts();
  if (directives != null) {
    port.liveScene.recordCellDirectives(directives);
    for (const [i, directive] of directives.entries()) {
      const tag = directive.tag;
      if (tag === "rebind") continue; // a full rebind is always safe
      if (tag === "skip") {
        // ONE evaluation, shared with `applyCells` below (memoized).
        const eligibility = activeHost.eligibility(i);
        const verdict = decideSkip(eligibility);
        if (verdict.kind === "rebind") rebindBecause[verdict.because] += 1;
        // Only the DOM axis disqualifies a claim from being REFEREED. The
        // scrubbed-to-0 header cell after a sheet switch is the canonical
        // case: its `skip` is content-correct and still not honorable, so
        // the live path rebinds and the still-executed full rebind would
        // report `fingerprint-moved` on the height it restores — no
        // evidence about the classifier either way. A LEDGER refusal is the
        // opposite: it is exactly what layer 1 exists to cross-check (and
        // while LR6 is dark it refuses every row, which would leave layer 1
        // nothing to check), so those cells stay sampled.
        if (!eligibility.domHonorable) continue;
      }
      const pre = port.liveScene.sampleCell()
        ? port.fingerprint(nonnull($cells[i], "cell referee: $cells[i]"))
        : null;
      // Layer 1 is the ledger's version verdict — meaningful even while the
      // render cursor is dark (its `shouldRebind` is hardwired true). Only a
      // `skip` consults it, so compute the raw decision for skips only.
      // `wouldSkip === false` is NOT by itself a divergence: it is also the
      // conservative default for a row the ledger cannot address, so the
      // three cases are reported apart (see `CursorEvidence`).
      let cursor: CursorEvidence = { kind: "agrees" };
      if (tag === "skip") {
        const rc = port.renderCursor();
        const decision = rc == null ? null : port.decideOnce(rc, nonnull(rows[i], "cell referee: rows[i]").id);
        cursor = cursorEvidence(decision);
      }
      pending.push({
        index: i, pre, cursor,
        reflowHonorable: tag === "resize" || tag === "span"
          ? cellReflowHonorable(i)
          : true,
      });
    }
  }

  // One directive per non-removed target cell, in target order; the host
  // resolves each occupant at its slot. Honor is the single SDK 2.0 switch,
  // read fresh each bind; while it is dark every cell rebinds,
  // byte-identical to the retired inline full-rebind loop.
  const applyStart = FrameMarks.begin();
  port.reconciler.applyCells(cellApply, activeHost, port.renderFlags().live());
  FrameMarks.end("binder.apply-cells", applyStart, rows.length, 0);

  if (directives != null && pending.length > 0) {
    const samples: CellRefereeSample[] = pending.map((p) => ({
      index: p.index,
      pre: p.pre,
      post: p.pre == null ? null : port.fingerprint(nonnull($cells[p.index], "cell referee: $cells[p.index]")),
      cursor: p.cursor,
      reflowHonorable: p.reflowHonorable,
    }));
    const cellReferee = refereeCells(directives, samples);
    port.liveScene.recordCellReferee(cellReferee);
    port.referees.recordCell(cellReferee);
    cellVerdict = cellReferee.verdict;
    // Same gating as the commit referee: `agree` is happy-path spam carrying
    // a fresh counters spread; `loud` is the alarm and always fires.
    if (port.renderFlags().diagnostics() || cellReferee.verdict !== "agree") {
      Tracing.log("scene/cell-referee", {
        column: defaultColumn.offset,
        verdict: cellReferee.verdict,
        firstDivergence: cellReferee.firstDivergence,
        rebindBecause,
        ...port.liveScene.snapshot(),
      });
    }
  }

  return cellVerdict;
}
